import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Filter, X, ChevronDown, MapPin, Calendar, Clock, TrendingUp } from 'lucide-react'
import { useMonuments } from '../../hooks/useMonuments'
import { useDebounce } from 'use-debounce'

const AdvancedSearch = ({ onSearch, onFilterChange, initialQuery = '' }) => {
  const [query, setQuery] = useState(initialQuery)
  const [debouncedQuery] = useDebounce(query, 500)
  const [showFilters, setShowFilters] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [recentSearches, setRecentSearches] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  
  const { searchMonuments, loading, filters, updateFilters } = useMonuments()

  // Filter options
  const eraOptions = [
    { value: 'all', label: 'All Eras' },
    { value: 'ancient', label: 'Ancient' },
    { value: 'classical', label: 'Classical' },
    { value: 'medieval', label: 'Medieval' },
    { value: 'early modern', label: 'Early Modern' },
    { value: 'modern', label: 'Modern' },
    { value: 'contemporary', label: 'Contemporary' },
    { value: 'unknown', label: 'Unknown' }
  ]

  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    { value: 'wikipedia', label: 'Wikipedia' },
    { value: 'wikidata', label: 'Wikidata' },
    { value: 'openstreetmap', label: 'OpenStreetMap' }
  ]

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'title', label: 'Title A-Z' },
    { value: 'era', label: 'Era' },
    { value: 'source', label: 'Source' }
  ]

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('historer_recent_searches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (error) {
        console.error('Error loading recent searches:', error)
      }
    }
  }, [])

  // Handle debounced search
  useEffect(() => {
    if (debouncedQuery.trim()) {
      performSearch(debouncedQuery)
    }
  }, [debouncedQuery])

  // Generate suggestions based on query
  useEffect(() => {
    if (query.length > 2) {
      generateSuggestions(query)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }, [query])

  const performSearch = useCallback(async (searchQuery) => {
    try {
      const results = await searchMonuments(searchQuery)
      
      if (onSearch) {
        onSearch(searchQuery, results)
      }

      // Add to recent searches
      if (searchQuery.trim() && !recentSearches.includes(searchQuery)) {
        const updated = [searchQuery, ...recentSearches.slice(0, 4)]
        setRecentSearches(updated)
        localStorage.setItem('historer_recent_searches', JSON.stringify(updated))
      }
    } catch (error) {
      console.error('Search error:', error)
    }
  }, [searchMonuments, onSearch, recentSearches])

  const generateSuggestions = async (searchQuery) => {
    // Simple suggestion logic - in production, you'd use an autocomplete API
    const commonTerms = [
      'monument', 'castle', 'church', 'temple', 'mosque', 'cathedral',
      'ruins', 'archaeological', 'historic', 'memorial', 'statue',
      'ancient', 'roman', 'greek', 'medieval', 'victorian', 'modern'
    ]
    
    const filtered = commonTerms
      .filter(term => term.toLowerCase().includes(searchQuery.toLowerCase()))
      .slice(0, 5)
    
    setSuggestions(filtered)
    setShowSuggestions(filtered.length > 0)
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setQuery(value)
    setIsExpanded(true)
  }

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion)
    setShowSuggestions(false)
    performSearch(suggestion)
  }

  const handleRecentSearchClick = (search) => {
    setQuery(search)
    performSearch(search)
  }

  const handleFilterChange = (filterType, value) => {
    updateFilters({ [filterType]: value })
    if (onFilterChange) {
      onFilterChange(filterType, value)
    }
    
    // Re-run search with new filters
    if (debouncedQuery.trim()) {
      performSearch(debouncedQuery)
    }
  }

  const handleClearFilters = () => {
    updateFilters({ era: '', source: '', sortBy: 'relevance' })
    if (onFilterChange) {
      onFilterChange('clear', null)
    }
    
    // Re-run search with cleared filters
    if (debouncedQuery.trim()) {
      performSearch(debouncedQuery)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (query.trim()) {
      performSearch(query)
      setShowSuggestions(false)
    }
  }

  const searchVariants = {
    collapsed: { width: '300px' },
    expanded: { width: '500px' }
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Main Search Bar */}
      <form onSubmit={handleSubmit} className="relative">
        <motion.div
          variants={searchVariants}
          animate={isExpanded ? 'expanded' : 'collapsed'}
          transition={{ duration: 0.3 }}
          className="relative"
        >
          <div className="relative flex items-center">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              id="monument-search"
              name="monument-search"
              value={query}
              onChange={handleInputChange}
              onFocus={() => setIsExpanded(true)}
              onBlur={() => {
                setTimeout(() => setIsExpanded(false), 200)
                setShowSuggestions(false)
              }}
              placeholder="Search monuments, places, historical events..."
              className="w-full pl-12 pr-24 py-4 bg-white border border-gray-300 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
              autoComplete="off"
            />
            
            {/* Action Buttons */}
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters || filters.era || filters.source
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="Filters"
              >
                <Filter className="w-4 h-4" />
              </button>
              
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                  title="Clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading || !query.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Suggestions Dropdown */}
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
              >
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                  >
                    <Search className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{suggestion}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Recent Searches */}
          {!showSuggestions && recentSearches.length > 0 && isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            >
              <div className="px-4 py-2 border-b border-gray-100">
                <h4 className="text-sm font-medium text-gray-600">Recent Searches</h4>
              </div>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleRecentSearchClick(search)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-3"
                >
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{search}</span>
                </button>
              ))}
            </motion.div>
          )}
        </motion.div>
      </form>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 bg-white border border-gray-200 rounded-lg shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={handleClearFilters}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Clear All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Era Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline w-4 h-4 mr-1" />
                  Era
                </label>
                <select
                  value={filters.era || 'all'}
                  onChange={(e) => handleFilterChange('era', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {eraOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Source Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  Source
                </label>
                <select
                  value={filters.source || 'all'}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {sourceOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp className="inline w-4 h-4 mr-1" />
                  Sort By
                </label>
                <select
                  value={filters.sortBy || 'relevance'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AdvancedSearch
